import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Lock, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useCart } from "../context/CartContext";
import { useCustomerAuth } from "../context/CustomerAuthContext";
import { formatINR } from "../lib/api";
import {
  placeOrderAsCustomer,
  placeOrderAsGuest,
  createRazorpayOrder,
  verifyRazorpayPayment,
  checkServiceability,
  formatApiError,
  listAddresses,
} from "../lib/customerApi";

const PAYMENT_METHODS = [
  { key: "razorpay", label: "Pay Online — UPI / Card / Netbanking", sub: "Secure checkout powered by Razorpay" },
  { key: "cod", label: "Cash on Delivery", sub: "Pay in cash when your order arrives" },
];

export default function Checkout() {
  const navigate = useNavigate();
  const { items, subtotal, clear } = useCart();
  const { user } = useCustomerAuth();

  const [shipping, setShipping] = useState({
    full_name: "",
    phone: "",
    email: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    pincode: "",
    country: "India",
  });
  const [coupon, setCoupon] = useState("");
  const [discount, setDiscount] = useState(0);
  const [couponCode, setCouponCode] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("razorpay");
  const [placing, setPlacing] = useState(false);
  const [serviceability, setServiceability] = useState(null); // {serviceable, cheapest_rate, carrier, etd} | null
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState("");

  useEffect(() => {
    if (user && user !== false) {
      listAddresses().then((list) => {
        setSavedAddresses(list);
        const def = list.find((a) => a.is_default) || list[0];
        if (def) {
          setSelectedAddressId(def.id);
          setShipping((s) => ({
            ...s,
            full_name: def.full_name,
            phone: def.phone,
            email: s.email || user.email || "",
            address_line1: def.address_line1,
            address_line2: def.address_line2 || "",
            city: def.city,
            state: def.state,
            pincode: def.pincode,
            country: def.country || "India",
          }));
        }
      });
    }
  }, [user]);

  const applySavedAddress = (id) => {
    setSelectedAddressId(id);
    if (id === "new") return;
    const a = savedAddresses.find((x) => x.id === id);
    if (!a) return;
    setShipping((s) => ({
      ...s,
      full_name: a.full_name,
      phone: a.phone,
      address_line1: a.address_line1,
      address_line2: a.address_line2 || "",
      city: a.city,
      state: a.state,
      pincode: a.pincode,
      country: a.country || "India",
    }));
  };

  useEffect(() => {
    if (user && user !== false) {
      setShipping((s) => ({
        ...s,
        full_name: s.full_name || user.name || "",
        phone: s.phone || user.phone || "",
        email: s.email || user.email || "",
      }));
    }
  }, [user]);

  useEffect(() => {
    if (!items.length && !placing) navigate("/cart");
  }, [items.length, navigate, placing]);

  // Pincode serviceability check (debounced)
  useEffect(() => {
    const pin = shipping.pincode;
    if (!pin || pin.length !== 6) {
      setServiceability(null);
      return;
    }
    setServiceability({ loading: true });
    const t = setTimeout(() => {
      checkServiceability(pin, paymentMethod === "cod")
        .then(setServiceability)
        .catch(() => setServiceability({ error: true }));
    }, 400);
    return () => clearTimeout(t);
  }, [shipping.pincode, paymentMethod]);

  // Pincode serviceability check (debounced)
  useEffect(() => {
    const pin = shipping.pincode;
    if (!pin || pin.length !== 6) {
      setServiceability(null);
      return;
    }
    setServiceability({ loading: true });
    const t = setTimeout(() => {
      checkServiceability(pin, paymentMethod === "cod")
        .then(setServiceability)
        .catch(() => setServiceability({ error: true }));
    }, 400);
    return () => clearTimeout(t);
  }, [shipping.pincode, paymentMethod]);

  const discountAmt = Math.round((subtotal * discount) / 100);
  const total = subtotal - discountAmt;

  const applyCoupon = async () => {
    const code = coupon.trim().toUpperCase();
    if (!code) return;
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/coupons/${encodeURIComponent(code)}`
      );
      if (!res.ok) throw new Error("invalid");
      const c = await res.json();
      setDiscount(c.percent_off);
      setCouponCode(c.code);
      toast.success(`Coupon ${c.code} applied — ${c.percent_off}% off`);
    } catch {
      setDiscount(0);
      setCouponCode(null);
      toast.error("Invalid coupon code");
    }
  };

  const set = (k, v) => setShipping((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setPlacing(true);
    const itemsPayload = items.map((i) => ({
      product_id: i.id,
      slug: i.slug,
      name: i.name,
      image: i.image,
      color: i.color,
      size: i.size,
      qty: i.qty,
      price: i.price,
    }));

    try {
      if (paymentMethod === "razorpay") {
        // 1. Create Razorpay order on our backend
        const rzpOrder = await createRazorpayOrder({
          items: itemsPayload,
          shipping,
          coupon_code: couponCode || null,
        });
        // 2. Open Razorpay Checkout modal
        if (!window.Razorpay) {
          toast.error("Payment script not loaded yet. Please retry in a moment.");
          setPlacing(false);
          return;
        }
        const options = {
          key: rzpOrder.key_id,
          amount: rzpOrder.amount,
          currency: rzpOrder.currency,
          order_id: rzpOrder.razorpay_order_id,
          name: "Asvasa",
          description: `Order ${rzpOrder.order_number}`,
          prefill: rzpOrder.prefill,
          theme: { color: "#1A1A1A" },
          modal: {
            ondismiss: () => {
              setPlacing(false);
              toast("Payment cancelled. Your cart is intact.", {
                description: `Order ${rzpOrder.order_number} kept as pending.`,
              });
            },
          },
          handler: async (response) => {
            try {
              const order = await verifyRazorpayPayment({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                internal_order_id: rzpOrder.internal_order_id,
              });
              navigate(
                `/order-confirmation?n=${encodeURIComponent(order.order_number)}&e=${encodeURIComponent(shipping.email)}`,
                { state: { order }, replace: true }
              );
              setTimeout(() => clear(), 50);
            } catch (err) {
              toast.error(formatApiError(err, "Payment verification failed"));
              setPlacing(false);
            }
          },
        };
        const rzp = new window.Razorpay(options);
        rzp.on("payment.failed", () => {
          toast.error("Payment failed. Please try again.");
          setPlacing(false);
        });
        rzp.open();
        return;
      }

      // COD flow
      const payload = {
        items: itemsPayload,
        shipping,
        coupon_code: couponCode || null,
        payment_method: paymentMethod,
      };
      const order = user && user !== false
        ? await placeOrderAsCustomer(payload)
        : await placeOrderAsGuest(payload);
      navigate(
        `/order-confirmation?n=${encodeURIComponent(order.order_number)}&e=${encodeURIComponent(shipping.email)}`,
        { state: { order }, replace: true }
      );
      setTimeout(() => clear(), 50);
    } catch (err) {
      toast.error(formatApiError(err, "Could not place order"));
      setPlacing(false);
    }
  };

  if (!items.length) return null;

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-10 md:py-16" data-testid="checkout-page">
      <Link to="/cart" className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-[#4A4A4A] hover:text-[#1A1A1A] mb-6">
        <ChevronLeft size={14} /> Back to cart
      </Link>
      <p className="eyebrow">Secure checkout</p>
      <h1 className="font-serif-display text-4xl md:text-6xl mt-3">Complete your order</h1>

      <form onSubmit={submit} className="grid lg:grid-cols-[1fr_420px] gap-10 md:gap-16 mt-10">
        <div className="space-y-10">
          {/* Contact / Shipping */}
          <section>
            <h2 className="font-serif-display text-3xl">Delivery details</h2>
            {savedAddresses.length > 0 && (
              <div className="mt-5" data-testid="checkout-saved-addresses">
                <span className="eyebrow block mb-2">Use a saved address</span>
                <div className="flex flex-wrap gap-2">
                  {savedAddresses.map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => applySavedAddress(a.id)}
                      className={`text-left border px-4 py-3 max-w-xs transition-colors ${
                        selectedAddressId === a.id ? "border-[#1A1A1A] bg-[#b8af92]" : "border-[#a89e80] hover:border-[#1A1A1A]/60"
                      }`}
                      data-testid={`checkout-saved-${a.id}`}
                    >
                      <p className="text-[11px] tracking-[0.22em] uppercase">
                        {a.label || "Address"}
                        {a.is_default && <span className="text-[#7A7A7A]"> · default</span>}
                      </p>
                      <p className="text-sm mt-1 leading-tight">{a.full_name}, {a.city} {a.pincode}</p>
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => applySavedAddress("new")}
                    className={`text-left border px-4 py-3 max-w-xs transition-colors ${
                      selectedAddressId === "new" ? "border-[#1A1A1A] bg-[#b8af92]" : "border-[#a89e80] hover:border-[#1A1A1A]/60"
                    }`}
                    data-testid="checkout-saved-new"
                  >
                    <p className="text-[11px] tracking-[0.22em] uppercase">+ Use a new address</p>
                  </button>
                </div>
              </div>
            )}
            <div className="mt-6 grid sm:grid-cols-2 gap-5">
              <Field label="Full name" required>
                <input required value={shipping.full_name} onChange={(e) => set("full_name", e.target.value)} className={inputCls} data-testid="checkout-name" />
              </Field>
              <Field label="Phone" required>
                <input required value={shipping.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} data-testid="checkout-phone" />
              </Field>
              <Field label="Email" required className="sm:col-span-2">
                <input required type="email" value={shipping.email} onChange={(e) => set("email", e.target.value)} className={inputCls} data-testid="checkout-email" />
              </Field>
              <Field label="Address" required className="sm:col-span-2">
                <input required value={shipping.address_line1} onChange={(e) => set("address_line1", e.target.value)} className={inputCls} data-testid="checkout-address1" />
              </Field>
              <Field label="Apt, suite (optional)" className="sm:col-span-2">
                <input value={shipping.address_line2} onChange={(e) => set("address_line2", e.target.value)} className={inputCls} />
              </Field>
              <Field label="City" required>
                <input required value={shipping.city} onChange={(e) => set("city", e.target.value)} className={inputCls} data-testid="checkout-city" />
              </Field>
              <Field label="State" required>
                <input required value={shipping.state} onChange={(e) => set("state", e.target.value)} className={inputCls} data-testid="checkout-state" />
              </Field>
              <Field label="Pincode" required>
                <input required value={shipping.pincode} onChange={(e) => set("pincode", e.target.value)} className={inputCls} data-testid="checkout-pincode" maxLength={6} />
                <ServiceabilityHint data={serviceability} />
              </Field>
              <Field label="Country">
                <input value={shipping.country} disabled className={inputCls} />
              </Field>
            </div>
          </section>

          {/* Payment */}
          <section>
            <h2 className="font-serif-display text-3xl">Payment</h2>
            <div className="mt-6 space-y-3">
              {PAYMENT_METHODS.map((pm) => (
                <label
                  key={pm.key}
                  className={`flex items-start gap-3 border px-5 py-4 cursor-pointer transition-colors ${
                    paymentMethod === pm.key ? "border-[#1A1A1A] bg-[#b8af92]" : "border-[#a89e80] hover:border-[#1A1A1A]/60"
                  }`}
                  data-testid={`pay-method-${pm.key}`}
                >
                  <input
                    type="radio"
                    name="pm"
                    value={pm.key}
                    checked={paymentMethod === pm.key}
                    onChange={() => setPaymentMethod(pm.key)}
                    className="mt-1 accent-[#1A1A1A]"
                  />
                  <div>
                    <p className="font-body text-[15px]">{pm.label}</p>
                    <p className="text-[11px] text-[#4A4A4A] tracking-wider mt-0.5">{pm.sub}</p>
                  </div>
                </label>
              ))}
            </div>
            <p className="text-[11px] text-[#7A7A7A] mt-4 flex items-center gap-2">
              <Lock size={12} /> Powered by Razorpay · 256-bit SSL · all major UPI apps, cards and netbanking accepted.
            </p>
          </section>
        </div>

        {/* Summary */}
        <aside className="bg-[#b8af92] p-7 md:p-8 h-fit lg:sticky lg:top-28" data-testid="checkout-summary">
          <h2 className="font-serif-display text-2xl mb-5">Order summary</h2>
          <div className="space-y-3 max-h-[300px] overflow-auto pr-2">
            {items.map((it) => (
              <div key={it.key} className="flex gap-3">
                <img src={it.image} alt={it.name} className="w-14 h-16 object-cover bg-[#a89e80]" />
                <div className="flex-1 text-sm">
                  <p className="leading-tight">{it.name}</p>
                  <p className="text-[11px] text-[#4A4A4A] tracking-wider mt-1">
                    {it.color} · Size {it.size} · ×{it.qty}
                  </p>
                </div>
                <p className="text-sm">{formatINR(it.price * it.qty)}</p>
              </div>
            ))}
          </div>

          <div className="mt-6 border-t border-[#a89e80] pt-5">
            <p className="eyebrow mb-2">Promo code</p>
            <div className="flex gap-2">
              <Input
                value={coupon}
                onChange={(e) => setCoupon(e.target.value)}
                placeholder="WELCOME10"
                className="rounded-none border-[#a89e80] bg-[#c3ba9e] text-sm"
                data-testid="checkout-coupon-input"
              />
              <Button type="button" onClick={applyCoupon} variant="outline" className="rounded-none border-[#1A1A1A] text-xs tracking-[0.22em] uppercase px-5" data-testid="checkout-coupon-apply">
                Apply
              </Button>
            </div>
          </div>

          <div className="mt-5 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-[#4A4A4A]">Subtotal</span><span>{formatINR(subtotal)}</span></div>
            {discount > 0 && (
              <div className="flex justify-between">
                <span className="text-[#4A4A4A]">Discount ({discount}%)</span>
                <span>−{formatINR(discountAmt)}</span>
              </div>
            )}
            <div className="flex justify-between"><span className="text-[#4A4A4A]">Shipping</span><span>Complimentary</span></div>
          </div>

          <div className="border-t border-[#a89e80] mt-4 pt-4 flex justify-between items-baseline">
            <span className="eyebrow">Total</span>
            <span className="font-serif-display text-3xl" data-testid="checkout-total">{formatINR(total)}</span>
          </div>

          <Button
            type="submit"
            disabled={placing}
            className="w-full mt-6 bg-[#1A1A1A] hover:bg-black text-[#c3ba9e] rounded-none py-6 tracking-[0.22em] uppercase text-xs btn-shimmer disabled:opacity-60"
            data-testid="place-order-button"
          >
            {placing ? "Placing order…" : `Place Order · ${formatINR(total)}`}
          </Button>
          <p className="text-[11px] text-[#7A7A7A] mt-3 text-center tracking-wider flex items-center justify-center gap-2">
            <Lock size={11} /> Encrypted checkout
          </p>
        </aside>
      </form>
    </div>
  );
}

const inputCls = "w-full border border-[#a89e80] bg-transparent px-3 py-2.5 text-[15px] outline-none focus:border-[#1A1A1A]";

function ServiceabilityHint({ data }) {
  if (!data) return null;
  if (data.loading) {
    return (
      <p className="mt-1.5 text-[11px] text-[#7A7A7A] flex items-center gap-1.5" data-testid="serviceability-loading">
        <Loader2 size={11} className="animate-spin" /> Checking serviceability…
      </p>
    );
  }
  if (data.error) return null; // silent on backend errors
  if (data.serviceable === null) return null;
  if (data.serviceable === false) {
    return (
      <p className="mt-1.5 text-[12px] text-[#9a3a3a] flex items-center gap-1.5" data-testid="serviceability-unavailable">
        <AlertTriangle size={12} /> We don't currently ship to this pincode. Please try a different address.
      </p>
    );
  }
  return (
    <p className="mt-1.5 text-[12px] text-[#3b6e4a] flex items-center gap-1.5" data-testid="serviceability-ok">
      <CheckCircle2 size={12} /> Delivers in ~{data.estimated_delivery || "5–7 days"} via {data.carrier}
    </p>
  );
}

function Field({ label, required, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="eyebrow block mb-2">
        {label}
        {required && <span className="text-[#8c6c3c]"> *</span>}
      </span>
      {children}
    </label>
  );
}
